/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import { cloneDeep, includes, mapValues } from 'lodash';
import moment from 'moment';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { applyMiddleware, compose, createStore } from 'redux';
import { createLogicMiddleware } from 'redux-logic';
import { GLOBALS } from 'services/globals';

import { DATE_DATA_FORMAT, PRIOR_YEAR, today } from 'services/Dates';
import { products, productTypes } from 'services/Products';

import {
  metadataInitialState,
  metadataReducer
} from 'state/api/MetadataReducer';

import {
  customerInitialState,
  customerReducer
} from 'state/customer/CustomerReducer';

import { tourReducer } from 'state/Tour/TourReducers';

import {
  customVideoInitialState,
  customVideoReducer,
  customVideoEligibilityReducer
} from 'state/customVideo/CustomVideoReducer';

import {
  accountVisibilitySaveButtonReducer,
  availableFundsInitialState,
  availableFundsReducer,
  contactInfoSaveButtonReducer,
  customerPreferencesInitialState,
  customerPreferencesReducer,
  dividendsCapitalGainsInitialState,
  dividendsCapitalGainsReducer,
  documentDeliveryReducer,
  mutualFundsInitialState,
  mutualFundsReducer,
  fixedFooterSaveOptionsReducer,
  updateShowAllReducer,
  selectedDefaultViewSettingsReducer,
  distributionToEditReducer
} from 'state/preferences/PreferencesReducer';

import {
  subscriptionsReducer,
  subscriptionsSaveReducer
} from 'state/subscriptions/SubscriptionsReducer';

import {
  beneficiariesReducer,
  isEditingBeneficiariesReducer,
  beneficiariesFormReducer,
  beneficiaryFormObj,
  managedBeneficiariesGroupV2Reducer,
} from 'state/beneficiaries/BeneficiaryReducer';

import { accountBalancesReducer } from 'state/account/AccountBalancesReducer';

import { requiredMinimumDistributionReducer } from 'state/distribution/RequiredMinimumDistributionReducer';
import { requiredMinimumDistributionSummaryReducer } from 'state/distribution/RequiredMinimumDistributionSummaryReducer';

import {
  rpsAccountBalancesReducer,
  workplaceYearEndBalanceReducer,
  workplaceYearEndBalanceInitialState,
  rpsBalancesInitialState
} from 'state/account/RpsAccountBalancesReducer';

import { hiddenAccountsReducer } from 'state/account/HiddenAccountsReducer';

import { accountOverviewTableReducer } from './account/AccountOverviewTableReducer';

import { externalInvestmentConsentReducer } from 'state/external/ExternalInvestmentConsentReducer';

import {
  initialCustomizeAccountViewFilteringState,
  initialFilteringState,
  selectAccountsFilteringReducer,
  setHistoricalBalances
} from './account/SelectAccountsFilteringReducer';
import { requestStatusReducer } from './requests/RequestStatusReducer';
import {
  historicalTransactionsReducer, historicalTransactionsReducerV2,
  initialTransactionsHistoryFilteringState,
  pendingTransactionsReducer, pendingTransactionsReducerV2,
  priorBusinessDayReducer,
  selectTransactionsHistoryFilteringReducer,
  selectTransactionsHistoryFilteringReducerV2,
  transactionsHistoryTableReducer,
  transactionsHistoryTableReducerV2,
  transactionsPendingTableReducer, transactionsPendingTableReducerV2
} from './transaction/TransactionReducers';

import {
  mmrMessagesReducer,
  notificationReducer
} from 'state/notification/NotificationReducer';

import {
  timelyAlertsInitialState,
  timelyAlertReducer
} from 'state/timelyAlert/TimelyAlertReducer';

import { viewInitialState, viewReducer } from 'state/view/ViewReducer';

import {
  initialLastRequestTime,
  lastRequestTimeReducer,
  ssoAuthentication
} from 'state/auth/AuthReducer';

import {
  externalInvestmentBalancesReducer,
  externalInvestmentRequestTimeReducer
} from 'state/external/ExternalInvestmentBalancesReducer';

import { IRTMessagesReducer } from 'state/IRT/IRTMessagesReducer';

import {
  contactMethodMaintenanceInitialState,
  contactMethodMaintenanceReducer
} from 'state/contactMethodMaintenance/ContactMethodMaintenanceReducer';

import {
  confidenceScoresReducer,
  initialConfidenceScores,
  initialMonteCarloConfidenceScores,
  MonteCarloConfidenceScoresReducer
} from 'state/account/ConfidenceScoresReducer';

import { accountHistory } from 'state/account/IndividualAccountReducer';

import {
  versionInitialState,
  versionReducer
} from 'state/version/VersionReducer';

import {
  initialStatementsFilterState,
  selectStatementsFilteringReducer,
  statementsReducer
} from 'state/statements/StatementsReducer';

import {
  nasuApplicationReducer,
  nasuApplicationReducerv2
} from 'state/nasuApplications/nasuApplicationReducer';

import { selectionsReducer } from 'state/selections/SelectionsReducer';

import {
  collegeSavingsActivitiesReducer,
  collegeSavingsFilterReducer,
  costBasisBalances,
  costBasisElections,
  iraContributionsReducer,
  iraDistributionsReducer,
  iraWithholdingsReducer,
  pendingCostBasisElectionsInitialState,
  pendingCostBasisElectionsReducer,
  priorYearIndicatorInitialState,
  priorYearIndicatorReducer,
  ytdTaxFilteringInitialState,
  ytdTaxFilteringReducer,
  ytdTaxInformationInitialState,
  ytdTaxInformationReducer
} from 'state/taxes/TaxReducers';

import {
  iraContributionsLimitInitialState,
  iraContributionsLimitReducer,
  iraContributionsPeriodInitialState,
  iraContributionsPeriodReducer
} from 'state/contributions/IRAContributionLimitReducers';

import {
  commentaryInitialState,
  commentaryReducer
} from 'state/commentary/CommentaryReducer';

import {
  contactInfoWasSavedReducer,
  invalidFieldsInitialState,
  invalidFieldsReducer,
  touchedFieldsInitialState,
  touchedFieldsReducer,
  validatedAddressInitialState,
  validatedAddressReducer
} from 'state/validation/ValidationReducers';

import { toasterInitialState, toasterReducer } from 'state/toast/ToastReducers';

import * as reducers from './Reducers';
import { prioritizedCombineReducers } from './ReduxHelpers';

import {
  sessionInitialState,
  sessionReducer
} from 'state/session/SessionReducer';

import logic from 'state/logic';

import {
  grantViewAccessInitialState,
  grantViewAccessReducer
} from 'state/grantViewAccess/GrantViewAccessReducers';
import {
  assetAllocationInitialState,
  assetAllocationReducer
} from 'state/assetAllocation/AssetAllocationReducers';
import {
  bankProfileInitialState,
  linkedBankAccountsReducer,
  bankAccountToUpdateReducer,
  updatedAddAssociationsReducer,
  updatedDeleteAssociationsReducer,
  bankVerificationsReducer,
  bankModalStateReducer,
  eligibleAgreementsForBankReducer,
  banksInErrorDepositVerificationReducer
} from 'state/linkedBankAccounts/LinkedBankAccountsReducer';
import {
  alertsAndNotificationsReducer,
  hasTextMessageConsentReducer,
  hasOptedOutOfTextAlertsReducer,
  hasDeactivationReasonReducer,
  alertsAndNotificationsSelectionsReducer,
  alertsAndNotificationsEmailReducer,
  alertsAndNotificationsPhoneNumbersReducer,
  alertsAndNotificationsSelectionsInitialState,
  isActivatedForTextAlertsReducer,
  mobileCarriersReducer
} from 'state/alertsAndNotifications/AlertsAndNotificationsReducer';
import {
  householdInformationInitialState,
  householdInformationReducer
} from 'state/household/HouseholdInformationReducer';
import {
  kiplingerRegistrationInitialState,
  kiplingerRegistrationReducer
} from 'state/kiplingerRegistration/KiplingerRegistrationReducer';
import {
  rpsSubscriptionInfoInitialState,
  rpsSubscriptionInformationReducer
} from 'state/LinkRpsAccounts/LinkRpsAccountsReducer';
import {
  morningstarInitialState,
  morningstarReducer
} from 'state/morningstar/MorningstarReducer';
import {
  accountsGridFiltersReducer,
  initialAccountsGridFilteringState,
  accountsGridPrintLayoutReducer
} from 'state/account/AccountsGridFilteringReducer';
import { totalPerformance } from './performance/PerformanceReducers';

import { thirdPartyModalReducer } from 'state/thirdParty/ThirdPartyModalReducer';
import { PerformancePageReducer } from './performance/PerformancePageReducer';
import { PortfolioGrowthFilterReducer } from './performance/PortfolioGrowthFilterReducer';
import { ActivitySummaryFilterReducer } from './performance/ActivitySummaryFilterReducer';
import { RateOfReturnFilterReducer } from './performance/RateOfReturnFilterReducer';
import {
  initialActivitySummaryFilter,
  initialPortfolioGrowthFilter,
  initialRateOfReturnFilter
} from 'services/Filters';

import {
    intraday,
    cdsPortfolio,
    unrealizedGainsLosses,
    realizedGainsLossesPrior,
    realizedGainsLossesCurrent,
    isPrintMode
} from './Reducers';
import { selectedAccountVisibilityReducer } from './account/SelectedAccountVisibilityReducer';
import { configReducer } from './config/ConfigReducer';
import { portfolioReducer } from './portfolio/PortfolioReducer';
import {isBrokerageOrderStatusTablesEnabled, nasuInprogressReturnJourneyTile} from 'services/FeatureFlags';
import {
  fundingMethodReducer,
  missionsReducer,
  missionUpdateReducer,
  STEP_MODAL_INITIAL_STATE,
  stepModalReducer
} from './missions/MissionsReducers';
import { brokerageReducer } from './brokerage/BrokerageReducer';
import {
  onboardingMaintenanceOptOutReducer,
  onboardingMaintenanceOptOutIntialState
} from 'state/onboardingMaintenance/OnboardingMaintenanceReducers';
import { bucketRollReducer } from "./bucketRoll/BucketRollReducer";
import {conversionHistoryInitialState, conversionHistoryReducer} from "./conversionHistory/ConversionHistoryReducers";

const initialSession = {
  id: null,
  userId: null
};

const transferredSession = {
  success: false
};

const accountFilters = {
  product: {
    label: 'Product'
  },
  purpose: {
    label: 'Purpose'
  }
};

const purposes = {
  retirement: {
    label: 'Retirement',
    userAccountNumbers: []
    /* key added dynamically */
  },
  nonRetirement: {
    label: 'Non-Retirement',
    userAccountNumbers: []
  }
};

Object.keys(purposes).forEach((key) => (purposes[key].key = key));

const accounts = [];

const initialReceivedAccounts = [];

const hiddenAccounts = [];

const visibleAccounts = [];

const rawTransactionsHistory = [];

const transactionsHistoryTable = [];

const pendingTransactions = [];

const transactionsPendingTable = [];

const documentReferences = [];

const beneficiaries = [];

// use null to differentiate not have data yet or not finished loading yet empty object/array when loading
const accountPerformanceAggregate = {
  asOfDate: today(DATE_DATA_FORMAT),
  inceptionDate: today(DATE_DATA_FORMAT),
  inceptionReturn: null,
  ytdReturn: null,
  threeMonthReturn: null,
  lastQuarterReturn: null,
  oneYearReturn: null,
  threeYearReturn: null,
  fiveYearReturn: null,
  tenYearReturn: null
};

const productPerformance = [];
const aggregatedProductPerformance = [];
const accountPerformance = null;
const accountPerformanceTable = null;

const performancePeriods = [
  {
    key: 'lastQuarterReturn',
    label: 'Last Calendar Quarter'
  },
  {
    key: 'ytdReturn',
    label: 'Year-to-Date'
  },
  {
    key: 'oneYearReturn',
    label: '1 Year'
  },
  {
    key: 'threeYearReturn',
    label: '3 Years'
  },
  {
    key: 'fiveYearReturn',
    label: '5 Years'
  },
  {
    key: 'tenYearReturn',
    label: '10 Years'
  },
  {
    key: 'inceptionReturn',
    label: 'Since Inception'
  }
];

// key = product type number (10, 40, etc), value = true
const clientProductTypes = {};

const accountsById = {};

const visibleAccountsById = {};

export const balances = {
  aggregate: 0.0,
  dailyChange: 0.0,
  dailyRate: 0.0,
  monthlyAvailable: false,
  monthlyChange: 0.0,
  monthlyRate: 0.0,
  yearlyAvailable: false,
  yearlyChange: 0.0,
  yearlyRate: 0.0,
  asOfDate: moment.utc().format(),
  beginningTotal: 0.0,
  retirement: {
    label: 'Retirement',
    aggregate: 0.0,
    percent: 0.0,
    products: cloneDeep(products)
  },
  nonRetirement: {
    label: 'Non-Retirement',
    aggregate: 0.0,
    percent: 0.0,
    products: cloneDeep(products)
  }
};

const selections = {
  accountsPreFilter: {
    all: true,
    subset: [],
    selectedCount: 0,
    totalCount: 0
  },
  productsPreFilter: {
    all: true,
    subset: {}
  },
  purposesPreFilter: {
    all: true,
    subset: {}
  },
  accountFilter: 'product',
  products: {
    all: true,
    subset: mapValues(productTypes, () => false)
  },
  purposes: {
    all: true,
    subset: mapValues(purposes, () => false)
  },
  brokerageOnly: false,
  activePlusOnly: false,
  period: 'last3Months',
  accounts: [],
  accountNumbers: {}
};

// This store object was created to keep track of filters selected in the mobile setting before the user clicks "Change Filters"
const mobileFilterSelections = cloneDeep(selections);

const history = [];

const summary = {
  beginningMarketValue: 0.0,
  endingMarketValue: 0.0,
  additions: 0.0,
  deductions: 0.0,
  income: 0.0,
  marketFluctuation: 0.0
};

const performancePage = {
  accountIdsByType: {}
};

const growth = [];

const metrics = {
  numOfAdditions: 0,
  numOfDeductions: 0,
  numOfIncome: 0
};

const activitySummaryTable = {};

const requestStatus = {};

const initialNotification = {
  messages: [],
  unreadMessageCount: 0
};

const initialRequiredMinimumDistribution = {
  customerEligible: false
};

const initialRequiredMinimumDistributionSummary = {
  rmdSummary: {
    customerEligible: false,
    customerPreEligible: false
  }
};

// exported for unit tests
export const initialState = {
  performancePage,
  portfolioGrowth: initialPortfolioGrowthFilter,
  activitySummary: initialActivitySummaryFilter,
  rateOfReturn: initialRateOfReturnFilter,
  accountFilteringSelections: initialFilteringState,
  accountsGridFilteringSelections: initialAccountsGridFilteringState,
  accountFilters,
  accountsGridPrintLayout: 'none',
  accountHistoricalDate: null,
  accountOverviewTable: [],
  totalPerformance: {},
  accountPerformanceAggregate,
  initialReceivedAccounts,
  accounts,
  accountsById,
  visibleAccountsById,
  accountPerformance,
  accountPerformanceTable,
  activitySummaryTable,
  balances,
  bucketRollData: {},
  clientProductTypes,
  commentary: commentaryInitialState,
  contactMethodMaintenance: contactMethodMaintenanceInitialState,
  confidenceScores: initialConfidenceScores,
  config: {},
  monteCarloConfidenceScores: initialMonteCarloConfidenceScores,
  customer: customerInitialState,
  customVideo: customVideoInitialState,
  customVideoEligibility: [],
  customerPreferences: customerPreferencesInitialState,
  beneficiariesByAccountType: [],
  customizeAccountsFilters: initialCustomizeAccountViewFilteringState,
  disableAccountVisibilitySave: true,
  disableContactInfoSave: false,
  hideFixedFooterSaveOptions: true,
  showAllAccounts: false,
  documentReferences,
  documentDelivery: {},
  growth,
  hiddenAccounts,
  selectedVisibilityAccounts: [],
  historicalBalances: {
    balances: null,
    selectedDate: null
  },
  isEditingBeneficiaries: false,
  history,
  initialSession,
  iraContributionLimit: iraContributionsLimitInitialState,
  iraContributionPeriod: iraContributionsPeriodInitialState,
  iraContributionYearToggle: PRIOR_YEAR.toString(),
  lastRequestTime: initialLastRequestTime,
  metadata: metadataInitialState,
  metrics,
  mobileFilterSelections,
  missions: [],
  missionUpdate: {},
  notification: initialNotification,
  onboardingMaintenanceOptOut: onboardingMaintenanceOptOutIntialState,
  orderStatus: [],
  pendingTransactions,
  performancePeriods,
  portfolio: {},
  priorYearIndicator: priorYearIndicatorInitialState,
  priorBusinessDay: null,
  productPerformance,
  aggregatedProductPerformance,
  products,
  productTypes,
  purposes,
  rawTransactionsHistory,
  requestStatus,
  requiredMinimumDistribution: initialRequiredMinimumDistribution,
  requiredMinimumDistributionSummary: initialRequiredMinimumDistributionSummary,
  rpsBalances: rpsBalancesInitialState,
  rpsYearEndBalance: workplaceYearEndBalanceInitialState,
  selections,
  statementsFilteringSelections: initialStatementsFilterState,
  startTour: false,
  stepModal: STEP_MODAL_INITIAL_STATE,
  summary,
  transactionsHistoryTable,
  transactionsHistoryFilteringSelections:
    initialTransactionsHistoryFilteringState,
  transactionsPendingTable,
  transferredSession,
  version: versionInitialState,
  view: viewInitialState,
  visibleAccounts,
  ytdTaxInformation: ytdTaxInformationInitialState,
  ytdTaxFiltering: ytdTaxFilteringInitialState,
  touchedFields: touchedFieldsInitialState,
  contactInfoWasSaved: false,
  invalidFields: invalidFieldsInitialState,
  validatedAddress: validatedAddressInitialState,
  toaster: toasterInitialState,
  session: sessionInitialState,
  selectedDefaultViewSettings: {
    selectedDefaultStartPage: 'dashboard',
    selectedDefaultGroupId: 20,
    customAccountGroups: []
  },
  pendingCostBasisElections: pendingCostBasisElectionsInitialState,
  dividendsCapitalGains: dividendsCapitalGainsInitialState,
  distributionToEdit: null,
  availableFunds: availableFundsInitialState,
  mutualFunds: mutualFundsInitialState,
  grantViewAccess: grantViewAccessInitialState,
  assetAllocation: assetAllocationInitialState,
  conversionHistory: conversionHistoryInitialState,
  bankProfile: bankProfileInitialState,
  bankAccountToUpdate: null,
  updatedAddAssociations: [],
  updatedDeleteAssociations: [],
  bankModalState: 'add',
  bankVerifications: [],
  eligibleAgreementsForBank: [],
  alertsAndNotifications: [],
  alertsAndNotificationsSelections:
    alertsAndNotificationsSelectionsInitialState,
  alertsAndNotificationsPhoneNumbers: [],
  alertsAndNotificationsEmail: '',
  hasTextMessageConsent: false,
  isActivatedForTextAlerts: false,
  hasDeactivationReasonCode: false,
  mobileCarriers: [],
  showConsentModal: false,
  timelyAlerts: timelyAlertsInitialState,
  householdInformation: householdInformationInitialState,
  kiplingerRegistration: kiplingerRegistrationInitialState,
  rpsSubscriptionInformation: rpsSubscriptionInfoInitialState,
  morningstarAssociation: morningstarInitialState,
  banksInErrorDepositVerification: [],
  ssoAuthentication: {},
  thirdPartyModal: {},
  beneficiariesForm: beneficiaryFormObj,
  beneficiaries,
  managedBeneficiaryGroupV2: {
    managedBeneficiaries: []
  }
};

const basename = GLOBALS.AAM_UI_TRP_APP_CONTEXT || '/';
export const browserHistory = createBrowserHistory({ basename });
const reactRouterMiddleware = routerMiddleware(browserHistory);
const middlewares = [reactRouterMiddleware, createLogicMiddleware(logic)];

const allowReduxDevToolsIn = ['local', 'dev', 'stage'];
let composeEnhancers = compose;
if (includes(allowReduxDevToolsIn, GLOBALS.AAM_UI_TRP_AWS_ENVIRONMENT)) {
  composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
}

export const allReducers = {
  router: connectRouter(browserHistory),
  ...reducers,
  accountOverviewTable: accountOverviewTableReducer,
  accountFilteringSelections: selectAccountsFilteringReducer,
  accountsGridFilteringSelections: accountsGridFiltersReducer,
  accountsGridPrintLayout: accountsGridPrintLayoutReducer,
  accountHistory,
  activitySummary: ActivitySummaryFilterReducer,
  alertsAndNotifications: alertsAndNotificationsReducer,
  alertsAndNotificationsSelections: alertsAndNotificationsSelectionsReducer,
  alertsAndNotificationsPhoneNumbers: alertsAndNotificationsPhoneNumbersReducer,
  alertsAndNotificationsEmail: alertsAndNotificationsEmailReducer,
  assetAllocation: assetAllocationReducer,
  conversionHistory: conversionHistoryReducer,
  availableFunds: availableFundsReducer,
  brokerage: brokerageReducer,
  balances: accountBalancesReducer,
  bankAccountToUpdate: bankAccountToUpdateReducer,
  banksInErrorDepositVerification: banksInErrorDepositVerificationReducer,
  bankModalState: bankModalStateReducer,
  bankProfile: linkedBankAccountsReducer,
  bankVerifications: bankVerificationsReducer,
  beneficiariesByAccountType: beneficiariesReducer,
  bucketRollData: bucketRollReducer,
  cdsPortfolio,
  unrealizedGainsLosses,
  isPrintMode,
  realizedGainsLossesPrior,
  realizedGainsLossesCurrent,
  collegeSavingsActivities: collegeSavingsActivitiesReducer,
  collegeSavingsFilter: collegeSavingsFilterReducer,
  commentary: commentaryReducer,
  confidenceScores: confidenceScoresReducer,
  config: configReducer,
  contactInfoWasSaved: contactInfoWasSavedReducer,
  contactMethodMaintenance: contactMethodMaintenanceReducer,
  costBasisBalances,
  costBasisElections,
  customer: customerReducer,
  customVideo: customVideoReducer,
  customVideoEligibility: customVideoEligibilityReducer,
  customerPreferences: customerPreferencesReducer,
  disableAccountVisibilitySave: accountVisibilitySaveButtonReducer,
  disableContactInfoSave: contactInfoSaveButtonReducer,
  dividendsCapitalGains: dividendsCapitalGainsReducer,
  distributionToEdit: distributionToEditReducer,
  documentDelivery: documentDeliveryReducer,
  documentReferences: statementsReducer,
  eligibleAgreementsForBank: eligibleAgreementsForBankReducer,
  externalInvestmentConsent: externalInvestmentConsentReducer,
  externalInvestmentBalances: externalInvestmentBalancesReducer,
  externalInvestmentRequestTime: externalInvestmentRequestTimeReducer,
  grantViewAccess: grantViewAccessReducer,
  hasDeactivationReasonCode: hasDeactivationReasonReducer,
  hasOptedOutOfTextAlerts: hasOptedOutOfTextAlertsReducer,
  hasTextMessageConsent: hasTextMessageConsentReducer,
  hiddenAccounts: hiddenAccountsReducer,
  selectedVisibilityAccounts: selectedAccountVisibilityReducer,
  hideFixedFooterSaveOptions: fixedFooterSaveOptionsReducer,
  historicalBalances: setHistoricalBalances,
  householdInformation: householdInformationReducer,
  intraday,
  invalidFields: invalidFieldsReducer,
  iraContributions: iraContributionsReducer,
  iraDistributions: iraDistributionsReducer,
  iraWithholdings: iraWithholdingsReducer,
  iraContributionLimit: iraContributionsLimitReducer,
  iraContributionPeriod: iraContributionsPeriodReducer,
  irtMessages: IRTMessagesReducer,
  isEditingBeneficiaries: isEditingBeneficiariesReducer,
  beneficiariesForm:beneficiariesFormReducer,
  isActivatedForTextAlerts: isActivatedForTextAlertsReducer,
  kiplingerRegistration: kiplingerRegistrationReducer,
  lastRequestTime: lastRequestTimeReducer,
  managedBeneficiaryGroupV2: managedBeneficiariesGroupV2Reducer,
  metadata: metadataReducer,
  mmrMessages: mmrMessagesReducer,
  mobileCarriers: mobileCarriersReducer,
  monteCarloConfidenceScores: MonteCarloConfidenceScoresReducer,
  morningstarAssociation: morningstarReducer,
  missions: missionsReducer,
  missionUpdate: missionUpdateReducer,
  mutualFunds: mutualFundsReducer,
  nasuApplications: nasuInprogressReturnJourneyTile()
    ? nasuApplicationReducerv2
    : nasuApplicationReducer,
  onboardingMaintenanceOptOut: onboardingMaintenanceOptOutReducer,
  fundingMethod: fundingMethodReducer,
  stepModal: stepModalReducer,
  notification: notificationReducer,
  pendingCostBasisElections: pendingCostBasisElectionsReducer,
  pendingTransactions: pendingTransactionsReducer,
  performancePage: PerformancePageReducer,
  portfolio: portfolioReducer,
  portfolioGrowth: PortfolioGrowthFilterReducer,
  priorBusinessDay: priorBusinessDayReducer,
  priorYearIndicator: priorYearIndicatorReducer,
  rateOfReturn: RateOfReturnFilterReducer,
  rawTransactionsHistory:  isBrokerageOrderStatusTablesEnabled() ? historicalTransactionsReducerV2 : historicalTransactionsReducer,
  rawPendingTransactionsHistory: pendingTransactionsReducerV2,
  requestStatus: requestStatusReducer,
  requiredMinimumDistribution: requiredMinimumDistributionReducer,
  requiredMinimumDistributionSummary: requiredMinimumDistributionSummaryReducer,
  rpsBalances: rpsAccountBalancesReducer,
  rpsSubscriptionInformation: rpsSubscriptionInformationReducer,
  rpsYearEndBalance: workplaceYearEndBalanceReducer,
  selectedDefaultViewSettings: selectedDefaultViewSettingsReducer,
  showAllAccounts: updateShowAllReducer,
  selections: selectionsReducer,
  session: sessionReducer,
  ssoAuthentication,
  statementsFilteringSelections: selectStatementsFilteringReducer,
  startTour: tourReducer,
  subscriptions: subscriptionsReducer,
  subscriptionsSave: subscriptionsSaveReducer,
  thirdPartyModal: thirdPartyModalReducer,
  timelyAlerts: timelyAlertReducer,
  touchedFields: touchedFieldsReducer,
  toaster: toasterReducer,
  totalPerformance,
  transactionsHistoryTable: isBrokerageOrderStatusTablesEnabled() ? transactionsHistoryTableReducerV2 : transactionsHistoryTableReducer,
  transactionsPendingTable: isBrokerageOrderStatusTablesEnabled() ? transactionsPendingTableReducerV2 : transactionsPendingTableReducer,
  transactionsHistoryFilteringSelections: isBrokerageOrderStatusTablesEnabled()
    ? selectTransactionsHistoryFilteringReducerV2
    : selectTransactionsHistoryFilteringReducer,
  updatedAddAssociations: updatedAddAssociationsReducer,
  updatedDeleteAssociations: updatedDeleteAssociationsReducer,
  view: viewReducer,
  version: versionReducer,
  ytdTaxInformation: ytdTaxInformationReducer,
  ytdTaxFiltering: ytdTaxFilteringReducer,
  validatedAddress: validatedAddressReducer
};

export const rootReducer = prioritizedCombineReducers(
  allReducers,
  initialState
);

export default createStore(
  rootReducer,
  initialState,
  composeEnhancers(applyMiddleware(...middlewares))
);
